import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {MessageService} from 'primeng/api';
import {AdTypesSettingsService} from '../../../../../service/ad-types-settings.service';
import {GroupAffiliationModel} from '../../../../../model/group-affiliation.model';
import {PreviousOwnershipModel} from '../../../../../model/previous-ownership.model';
import {ConditionalIncentiveModel} from '../../../../../model/conditional-incentive.model';
import {NavigationStart, Router} from '@angular/router';
import {Subscription} from 'rxjs';

@Component({
  selector: 'auto-conditional-incentive-configurator',
  templateUrl: './conditional-incentive-configurator.component.html',
  styleUrls: ['./conditional-incentive-configurator.component.scss']
})
export class ConditionalIncentiveConfiguratorComponent implements OnInit, OnChanges {
  @Input() accountId: number;
  @Input() conditionalIncentiveFeatureFlagEnabled: boolean;
  @Input() applicationValues;
  public groupAffiliations: GroupAffiliationModel[] = [];
  public previousOwnerships: PreviousOwnershipModel[] = [];
  @Input() initialValue: ConditionalIncentiveModel[] = [];
  @Output() formDirty = new EventEmitter<boolean>();
  @Output() formValid = new EventEmitter<boolean>();

  public incentiveForm: FormGroup;
  public isFormLoaded = false;
  public isLoading = true;
  public isSaving = false;
  private deletedIncentives: ConditionalIncentiveModel[] = [];
  private routerSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private adTypesSettingsService: AdTypesSettingsService,
    private messageService: MessageService
  ) {
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && this.incentiveForm.dirty) {
        if (confirm('You have unsaved changes. Do you really want to leave?')) {
          this.incentiveForm.markAsPristine();
        } else {
          this.router.navigateByUrl(this.router.url);
        }
      }
    });
  }

  ngOnInit(): void {
    this.incentiveForm = this.formBuilder.group({
      incentives: this.formBuilder.array([])
    });

    this.loadData();
  }

  /**
   * Loads the initial values when form changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialValue && this.incentiveForm) {
      this.loadInitialValues();
    }
  }

  /**
   * Load data from the backend
   */
  loadData(): void {
    this.isLoading = true;
    this.adTypesSettingsService.getConditionalIncentiveOptions()
      .subscribe(
        (response) => {
          this.groupAffiliations = response.groupAffiliations.sort((firstItem, secondItem) => { return firstItem.name.localeCompare(secondItem.name);});
          this.previousOwnerships = response.previousOwnerships.sort((firstItem, secondItem) => { return firstItem.name.localeCompare(secondItem.name);});
          this.loadConditionalIncentives();
        },
        (error) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load conditional incentive options.' + error,
            life: 5000
          });
        }
      );
  }

  /**
   * Load incentives for the current account
   */
  loadConditionalIncentives(): void {
    this.adTypesSettingsService.getConditionalIncentives(this.accountId)
      .subscribe(
        (response) => {
          this.initialValue = response;
          this.initForm();
          this.isLoading = false;
        },
        (error) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load conditional incentives.',
            life: 5000
          });
          this.initForm();
        }
      );
  }

  /**
   * Initialize the form and set up value change subscription
   */
  initForm(): void {
    this.loadInitialValues();
    this.incentives.setValidators(this.validateDuplicateGroupOwnershipCombinations());
    this.incentiveForm.valueChanges.subscribe(() => {
      if (this.incentiveForm.dirty) {
        this.formDirty.emit(true);
      }
      this.formValid.emit(this.incentiveForm.valid);
    });
    this.isFormLoaded = true;
  }

  /**
   * Load initial values into the form
   */
  loadInitialValues(): void {
    while (this.incentives.length) {
      this.incentives.removeAt(0);
    }
    if (this.initialValue && this.initialValue.length) {
      this.initialValue.forEach((incentive) => {
        this.incentives.push(this.createIncentiveFormGroup(incentive));
      });
    }
    this.deletedIncentives = [];
  }

  /**
   * Create a form group for a single incentive
   */
  createIncentiveFormGroup(incentive?: ConditionalIncentiveModel): FormGroup {
    return this.formBuilder.group({
      id: [incentive?.id || null],
      accountId: [this.accountId],
      groupAffiliation: [this.groupAffiliations.find((affiliation) => {
        return affiliation.id === incentive?.groupAffiliationId;
      }) || null, Validators.required],
      previousOwnership: [this.previousOwnerships.find((ownership) => {
        return ownership.id === incentive?.previousOwnershipId;
      }) || null, Validators.required],
      applicationValue: [incentive?.applicationValue || null, Validators.required]
    });
  }

  /**
   * Get the incentives form array
   */
  get incentives(): FormArray {
    return this.incentiveForm.get('incentives') as FormArray;
  }

  /**
   * Add a new blank incentive row
   */
  addIncentive(): void {
    const newIncentive = new ConditionalIncentiveModel();
    newIncentive.accountId = this.accountId;

    this.incentives.push(this.createIncentiveFormGroup(newIncentive));
    this.incentiveForm.markAsDirty();
    this.formDirty.emit(true);
  }

  /**
   * Remove an incentive at the specified index
   */
  removeIncentive(index: number): void {
    const incentive = this.incentives.at(index).value;
    if (incentive.id) {
      const deletedIncentive = new ConditionalIncentiveModel();
      deletedIncentive.id = incentive.id;
      deletedIncentive.accountId = this.accountId;
      deletedIncentive.groupAffiliationId = incentive.groupAffiliation?.id;
      deletedIncentive.previousOwnershipId = incentive.previousOwnership?.id;
      deletedIncentive.applicationValue = incentive.applicationValue;
      this.deletedIncentives.push(deletedIncentive);
      this.formDirty.emit(true);
    }

    this.incentives.removeAt(index);
    this.incentiveForm.markAsDirty();
  }

  /**
   * Save the conditional incentives
   */
  saveIncentives(): void {
    if (this.incentiveForm.invalid) {
      this.markFormGroupTouched(this.incentiveForm);
    } else {
      this.handleIncentiveConfigurationSave();
    }
  }

  /**
   * Handles the incentive configuration save
   */
  handleIncentiveConfigurationSave() {
    this.isSaving = true;
    const incomingConfigurations: ConditionalIncentiveModel[] = [];
    this.incentives.controls.forEach((control) => {
      const formValue = control.value;
      const incentive = new ConditionalIncentiveModel();
      incentive.id = formValue.id || 0;
      incentive.accountId = this.accountId;
      incentive.groupAffiliationId = formValue.groupAffiliation?.id;
      incentive.previousOwnershipId = formValue.previousOwnership?.id;
      incentive.applicationValue = formValue.applicationValue;
      incomingConfigurations.push(incentive);
    });

    const payload = {
      incomingConfigurations: incomingConfigurations,
      deletedConfigurations: this.deletedIncentives
    };

    this.adTypesSettingsService.saveConditionalIncentives(payload).subscribe(
      async () => {
        this.loadConditionalIncentives();
        this.incentiveForm.markAsPristine();
        this.formDirty.emit(false);
        this.isSaving = false;
      },
      (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save conditional incentives.',
          life: 5000
        });
        this.isSaving = false;
      }
    );
  }

  /**
   * Mark all controls in a form group as touched
   */
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  /**
   * Validator function to check for duplicate combinations of group affiliation and previous ownership
   */
  private validateDuplicateGroupOwnershipCombinations(): ValidatorFn {
    return (rootControl: AbstractControl): ValidationErrors | null => {
      let validationResult: ValidationErrors | null = null;
      if (rootControl instanceof FormArray) {
        const seenCombinations = new Set<string>();
        let anyDuplicatesFound = false;

        for (const formGroup of rootControl.controls) {
          if (formGroup instanceof FormGroup) {
            const combinationInfo = this.extractGroupOwnershipCombinationKey(formGroup);

            if (combinationInfo.exists) {
              if (seenCombinations.has(combinationInfo.key)) {
                anyDuplicatesFound = true;
                this.updateDuplicateCombinationError(formGroup, true);
              } else {
                seenCombinations.add(combinationInfo.key);
                this.updateDuplicateCombinationError(formGroup, false);
              }
            }
          }
        }
        if (anyDuplicatesFound) {
          validationResult = { duplicateCombinations: true };
        }
      }

      return validationResult;
    };
  }

  /**
   * Reads the `groupAffiliation.id` and `previousOwnership.id` from a FormGroup
   * and returns a string key if both IDs exist.
   */
  private extractGroupOwnershipCombinationKey(group: FormGroup): { exists: boolean; key: string } {
    let exists = false;
    let key = '';

    const groupAffiliationControl = group.get('groupAffiliation');
    const previousOwnershipControl = group.get('previousOwnership');

    if (groupAffiliationControl && previousOwnershipControl) {
      const groupAffiliationValue = groupAffiliationControl.value;
      const previousOwnershipValue = previousOwnershipControl.value;

      if (
        groupAffiliationValue && typeof groupAffiliationValue === 'object' && 'id' in groupAffiliationValue &&
        previousOwnershipValue && typeof previousOwnershipValue === 'object' && 'id' in previousOwnershipValue
      ) {
        const groupAffiliationId = (groupAffiliationValue as any).id;
        const previousOwnershipId = (previousOwnershipValue as any).id;

        if (groupAffiliationId && previousOwnershipId) {
          key = String(groupAffiliationId) + '-' + String(previousOwnershipId);
          exists = true;
        }
      }
    }

    return { exists, key };
  }

  /**
   * Adds or removes the `duplicateCombination` error for a given FormGroup
   * while preserving any other existing errors.
   */
  private updateDuplicateCombinationError(group: FormGroup, isDuplicate: boolean): void {
    const currentErrors = group.errors;
    let updatedErrors: ValidationErrors = {};
    if (currentErrors) {
      updatedErrors = { ...currentErrors };
    }

    if (isDuplicate) {
      updatedErrors.duplicateCombination = true;
    } else {
      if (Object.prototype.hasOwnProperty.call(updatedErrors, 'duplicateCombination')) {
        delete updatedErrors.duplicateCombination;
      }
    }

    let nextErrors: ValidationErrors | null = null;
    if (Object.keys(updatedErrors).length > 0) {
      nextErrors = updatedErrors;
    }
    group.setErrors(nextErrors);
  }
}
